import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import Button from "../../atoms/Button";
import { COLOR_SYSTEM } from "../../design/design-system";

const FloatingButtonContainer = styled.div<{ top: number; left: number }>`
  position: fixed;
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
  z-index: 1000;
  opacity: 0;
  animation: fadeIn 0.2s ease-out forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const StyledButton = styled(Button)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid ${COLOR_SYSTEM.get("Blue")?.[300]} !important;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

interface SelectionBasedReferenceButtonProps {
  containerRef: React.RefObject<HTMLElement>;
  isEditMode: boolean;
  onInsertReference?: (referenceId: string) => void;
}

const SelectionBasedReferenceButton: React.FC<SelectionBasedReferenceButtonProps> = ({
  containerRef,
  isEditMode,
  onInsertReference,
}) => {
  const [selectedText, setSelectedText] = useState("");
  const [nearestId, setNearestId] = useState<string | null>(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const selectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 가장 가까운 ID를 가진 요소 찾기
  const findNearestId = useCallback(
    (selection: Selection): string | null => {
      if (!selection.anchorNode || !containerRef.current) return null;

      let currentNode: Node | null = selection.anchorNode;
      let element: HTMLElement | null = null;

      // 텍스트 노드인 경우 부모 요소 찾기
      if (currentNode.nodeType === Node.TEXT_NODE) {
        element = currentNode.parentElement;
      } else {
        element = currentNode as HTMLElement;
      }

      // 상위로 올라가면서 ID를 가진 요소 찾기
      while (element && element !== containerRef.current) {
        if (element.id) {
          // 참조 가능한 ID 패턴 확인
          const isValidRef =
            // 허가사항 패턴
            element.id.match(
              /^(INGREDI_|EE_|UD_|NB_|ACTN_|STOR_|EXPR_|ELSE_|UNDEF_|WARN|CONT|CARE|SIDE|DDIN|ADMN|OVER|CLIX|KDNY|LIVR|PRD|PRCU)[A-Z0-9_]*/
            ) ||
            // SPST 패턴
            element.id.match(/^(STOP_|START_|SPSTv3_)/) ||
            // BEERS, POLY 등
            element.id.match(/^(BEERS_|POLY_|CCMM_|LCMM_|KABS_)/);

          if (isValidRef) {
            return element.id;
          }
        }
        element = element.parentElement;
      }

      // ID를 가진 요소를 찾지 못한 경우, 가장 가까운 형제 요소 확인
      element =
        selection.anchorNode.nodeType === Node.TEXT_NODE
          ? selection.anchorNode.parentElement
          : (selection.anchorNode as HTMLElement);

      if (element) {
        // 이전 형제 요소들 확인
        let sibling = element.previousElementSibling;
        while (sibling) {
          if (
            sibling.id &&
            sibling.id.match(
              /^(INGREDI_|EE_|UD_|NB_|ACTN_|STOR_|EXPR_|ELSE_|UNDEF_|STOP_|START_|SPSTv3_|BEERS_|POLY_|CCMM_|LCMM_|KABS_)/
            )
          ) {
            return sibling.id;
          }
          sibling = sibling.previousElementSibling;
        }
      }

      return null;
    },
    [containerRef]
  );

  const handleSelectionChange = useCallback(() => {
    // 기존 타이머 취소
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    // 약간의 딜레이 후 선택 확인 (텍스트 드래그 중에는 버튼이 나타나지 않도록)
    selectionTimeoutRef.current = setTimeout(() => {
      const selection = window.getSelection();

      if (!selection || !isEditMode || !containerRef.current) {
        setIsVisible(false);
        return;
      }

      const selectedString = selection.toString().trim();

      if (selectedString) {
        // 선택된 텍스트가 컨테이너 내부에 있는지 확인
        const range = selection.getRangeAt(0);
        const commonAncestor = range.commonAncestorContainer;

        let isInsideContainer = false;
        let node: Node | null = commonAncestor;

        while (node) {
          if (node === containerRef.current) {
            isInsideContainer = true;
            break;
          }
          node = node.parentNode;
        }

        if (isInsideContainer) {
          setSelectedText(selectedString);
          const id = findNearestId(selection);

          if (id) {
            setNearestId(id);

            // 버튼 위치 계산 (선택 영역 아래)
            const rect = range.getBoundingClientRect();
            setButtonPosition({
              top: rect.bottom + window.scrollY + 5,
              left: rect.left + window.scrollX + rect.width / 2 - 50, // 버튼 중앙 정렬
            });

            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
        } else {
          setIsVisible(false);
        }
      } else {
        setIsVisible(false);
      }
    }, 200);
  }, [isEditMode, containerRef, findNearestId]);

  useEffect(() => {
    if (!isEditMode) {
      setIsVisible(false);
      return;
    }

    // 선택 변경 이벤트 리스너
    document.addEventListener("selectionchange", handleSelectionChange);

    // 클릭 이벤트로 선택 해제 감지
    const handleClick = (e: MouseEvent) => {
      // 버튼 클릭이 아닌 경우에만 숨기기
      const target = e.target as HTMLElement;
      if (!target.closest(".reference-insert-button")) {
        setTimeout(() => {
          const selection = window.getSelection();
          if (!selection || !selection.toString().trim()) {
            setIsVisible(false);
          }
        }, 100);
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      document.removeEventListener("click", handleClick);
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, [isEditMode, handleSelectionChange]);

  const handleInsert = () => {
    if (nearestId && onInsertReference) {
      onInsertReference(nearestId);

      // 선택 해제
      window.getSelection()?.removeAllRanges();
      setIsVisible(false);
      setSelectedText("");
      setNearestId(null);
    }
  };

  if (!isVisible || !nearestId || !onInsertReference) {
    return null;
  }

  return (
    <FloatingButtonContainer top={buttonPosition.top} left={buttonPosition.left}>
      <StyledButton
        className="reference-insert-button"
        type="Text"
        size="Small"
        color="Primary"
        variant="Outlined"
        onClick={handleInsert}
      >
        &lt; 참조삽입
      </StyledButton>
    </FloatingButtonContainer>
  );
};

export default SelectionBasedReferenceButton;
